import {DateInterval, Money} from '@udemy/graphql';
import {useI18n} from '@udemy/i18n';
import {Button, Link} from '@udemy/react-core-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {usePersonalPlanCollectionStats} from '@udemy/subscription-browse';
import {
    ConsumerSubscriptionPlan,
    SubscriptionPlanProductType,
    SubscriptionPlansProvider,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useFormatCurrency} from '@udemy/ud-data';

import {useLocalStorageDismiss} from 'src/hooks/use-local-storage-dismiss';
import {VALUE_PLAN_UPSELL} from 'src/lib/experimentation-constants';
import {SubscriptionEnrollment} from 'src/lib/fetchers';

import styles from './value-plan-upsell-banner.module.less';

const VALUE_PLAN_UPSELL_DISMISSED_KEY = 'value_plan_upsell_banner_dismissed';
const VALUE_PLAN_UPSELL_DISMISS_TTL_DAYS = 3;

interface ValuePlanUpsellBannerProps {
    isPaidSubscriber: boolean;
    subscriptionEnrollment?: SubscriptionEnrollment;
}

const findMatchingConsumerPlan = (
    renewalInterval: DateInterval,
    consumerSubscriptionPlan: ConsumerSubscriptionPlan,
) => {
    return consumerSubscriptionPlan.priceOptions.find(
        (priceOption) =>
            priceOption.renewalInterval.type === renewalInterval.type &&
            priceOption.renewalInterval.count === renewalInterval.count,
    );
};

const ValuePlanUpsellBannerContent = ({subscriptionEnrollment}: ValuePlanUpsellBannerProps) => {
    const {gettext, interpolate} = useI18n();
    const {formatCurrency} = useFormatCurrency();
    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const consumerSubscriptionPlanCollectionStats = usePersonalPlanCollectionStats();
    const {featureVariantAssignments, isLoading: experimentLoading} = useFeatures([
        VALUE_PLAN_UPSELL,
    ]);
    const [isDismissed, dismissBanner] = useLocalStorageDismiss(VALUE_PLAN_UPSELL_DISMISSED_KEY, {
        ttlDays: VALUE_PLAN_UPSELL_DISMISS_TTL_DAYS,
    });

    const valuePlanUpsellFeature =
        featureVariantAssignments.get(VALUE_PLAN_UPSELL)?.configuration ?? {};

    const isLoading =
        experimentLoading ||
        !consumerSubscriptionPlan ||
        consumerSubscriptionPlan?.isLoading ||
        !consumerSubscriptionPlanCollectionStats ||
        consumerSubscriptionPlanCollectionStats?.isLoading;

    if (isLoading || isDismissed || !valuePlanUpsellFeature.enableLIHPBanner) {
        return null;
    }

    const currentChargePrice = subscriptionEnrollment?.billing?.chargePrice as Money;
    const matchingConsumerPlan = findMatchingConsumerPlan(
        subscriptionEnrollment?.renewalInterval as DateInterval,
        consumerSubscriptionPlan as ConsumerSubscriptionPlan,
    );

    if (
        !matchingConsumerPlan ||
        !matchingConsumerPlan.price ||
        !matchingConsumerPlan.price.currency ||
        matchingConsumerPlan.price?.currency !== currentChargePrice.currency
    ) {
        return null;
    }

    const upsellPrice = matchingConsumerPlan.price.list - currentChargePrice.amount;
    const formattedUpsellPrice = formatCurrency(upsellPrice, {
        currency: matchingConsumerPlan.price.currency,
    });
    const upsellCourseCount = consumerSubscriptionPlanCollectionStats.numberOfCourses;
    return (
        <div className={'component-margin'}>
            <div
                data-testid="value-plan-upsell-banner"
                className={styles['value-plan-upsell-banner']}
            >
                <svg
                    className={styles['background']}
                    width="317"
                    height="100"
                    viewBox="0 0 317 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        opacity="0.5"
                        d="M82.0091 -53.0483C41.4704 -12.2966 -23.8201 61.3449 39.3271 29.8973C118.261 -9.41204 276.739 -113.034 244.807 -85.5944C212.875 -58.1546 17.4443 69.4955 96.5085 49.666C175.573 29.8365 344.241 -45.4615 255.215 6.76862C166.188 58.9988 182.427 94.9087 230.399 71.4915C278.371 48.0743 313.496 34.4697 313.496 34.4697"
                        stroke="#D2CAFF"
                        strokeWidth="26.0761"
                        strokeLinecap="round"
                    />
                </svg>
                <div className={styles['content']}>
                    <div className={'ud-heading-lg'}>
                        {gettext("You're enrolled in Starter Plan — Want even more?")}
                    </div>
                    <div className={styles['summary']}>
                        {interpolate(
                            gettext('For %(amount)s more get access to %(courses)s courses'),
                            {amount: formattedUpsellPrice, courses: upsellCourseCount},
                            true,
                        )}
                    </div>
                </div>
                <div className={styles['ctas']}>
                    <Button
                        componentClass={Link}
                        to="/subscription-checkout/upgrade/?upgrade_type=tier&product_type=CONSUMERSUBSCRIPTION"
                    >
                        {gettext('Upgrade to Personal Plan')}
                    </Button>
                    <Button
                        udStyle="ghost"
                        className="ud-link-neutral"
                        size="small"
                        onClick={dismissBanner}
                    >
                        {gettext('Keep Starter Plan')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const ValuePlanUpsellBanner = (props: ValuePlanUpsellBannerProps) => (
    <SubscriptionPlansProvider
        subscriptionPlanQueryVariables={[
            {filters: {productType: SubscriptionPlanProductType.Consumersubscription}},
        ]}
    >
        <ValuePlanUpsellBannerContent {...props} />
    </SubscriptionPlansProvider>
);
